import React, { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useThemeMode } from '../../design-system/theme';
import useABTest from '../../hooks/useABTest';
import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import {
  EducationDesignPreview,
  normalizeEducationDesignVariant,
} from '../education/EducationDesignExperiment';
import NextChallengePage from './NextChallengePage';
import { resolveChallengeAdMatchCopy } from './challengeAdMatchCopy';
import { ChallengeFormFirstCopyContext } from './challengeFormFirstCopy';
import {
  CHALLENGE_TRIMMED_COPY,
  CHALLENGE_TRIMMED_HEADLINE_COPY,
  ChallengeTrimmedCopyContext,
  type ChallengeTrimmedCopy,
} from './challengeTrimmedCopy';
import {
  CHALLENGE_ABC_EXPERIMENT,
  CHALLENGE_CLARITY_HOME_EXPERIMENT,
  decideChallengeAssignment,
  normalizeChallengeDesign,
  type ChallengeDesignArm,
  type ChallengeExperimentContext,
  type ChallengeFlagReading,
} from './challengePageExperiment';
import './ChallengePageClarityHome.css';
import useChallengeExperimentExposure, {
  getChallengeExperimentVersion,
} from './useChallengeExperimentExposure';

// The JavaScript hook exposes this stable contract; its legacy JSDoc returns Object.
// Keep the boundary explicit, as in EducationDesignExperiment.
type ChallengeAssignment = ChallengeFlagReading & {
  isPreviewOverride: boolean;
};

/** Arms painted on B's form-first page. C differs from B only in its copy. */
const FORM_FIRST_ARMS: readonly ChallengeDesignArm[] = ['form_first', 'form_first_ad_match'];
const UNASSIGNED_VARIANT = 'unassigned';
/** Arms painted on A's page with trimmed copy (Rob, 2026-10-20). */
const trimmedCopyFor = (arm: ChallengeDesignArm): ChallengeTrimmedCopy | null => {
  if (arm === 'trimmed') return CHALLENGE_TRIMMED_COPY;
  if (arm === 'trimmed_headline') return CHALLENGE_TRIMMED_HEADLINE_COPY;
  return null;
};

/** A new assignment replaces the old design wrapper atomically, never its history. */
export default function ChallengeClarityHomeExperiment({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const location = useLocation();
  const [theme] = useThemeMode();
  const isTarget = location.pathname.replace(/\/+$/, '') === '/challenges';
  const previewHost = isTarget && isPreviewExperimentOverrideHost();
  const searchParams = new URLSearchParams(location.search);
  const preview = previewHost
    ? normalizeChallengeDesign(searchParams.get('challengeDesign'))
    : null;
  // An explicit design preview of the existing experiment (?educationDesign=)
  // goes straight to that route. It never waits on these flags.
  const existingRoutePreview =
    previewHost && !preview && Boolean(searchParams.get('educationDesign'));
  const skipAll = !isTarget || Boolean(preview) || existingRoutePreview;
  const forcedOff = !isTarget || existingRoutePreview ? 'control' : undefined;

  // Rob, 2026-10-02: the A/B/C test runs on its own new flag, read first. A
  // preview of one of its arms is applied here without a flag read.
  const abcPreview = CHALLENGE_ABC_EXPERIMENT.posthogVariants.find((arm) => arm === preview);
  const abcTracking = useMemo(
    () => ({
      page: location.pathname,
      challenge_path: location.pathname,
      surface: 'challenge_landing_page',
      education_theme: theme,
      validVariants: CHALLENGE_ABC_EXPERIMENT.posthogVariants,
      skipFeatureFlagEvaluation: skipAll,
      forcedVariant: forcedOff || (preview ? abcPreview || 'control' : undefined),
      trackForcedExposure: false,
      trackExposure: false,
    }),
    [location.pathname, theme, skipAll, forcedOff, preview, abcPreview],
  );
  // SAFETY: useABTest returns these four fields on every path; the JS hook
  // normalizes variants to string, boolean, or null and supplies boolean state.
  const abc = useABTest(
    CHALLENGE_ABC_EXPERIMENT.name,
    CHALLENGE_ABC_EXPERIMENT.featureFlag,
    'control',
    abcTracking,
  ) as ChallengeAssignment;
  const abcDecision = decideChallengeAssignment(abc, {
    variant: null,
    isLoading: false,
    isExperimentActive: false,
  });

  // Martin's flag decides exactly as before, but only once the A/B/C flag has
  // answered without one of its arms. Until then it is held unassigned with no
  // read and no exposure, so a visitor the new test takes is never counted in
  // Martin's experiment.
  const originalReadEnabled = !skipAll && abcDecision.status === 'unassigned';
  const originalTracking = useMemo(
    () => ({
      page: location.pathname,
      challenge_path: location.pathname,
      surface: 'challenge_landing_page',
      education_theme: theme,
      validVariants: CHALLENGE_CLARITY_HOME_EXPERIMENT.posthogVariants,
      skipFeatureFlagEvaluation: skipAll || !originalReadEnabled,
      forcedVariant: preview || (originalReadEnabled ? undefined : UNASSIGNED_VARIANT),
      trackForcedExposure: false,
      trackExposure: false,
    }),
    [location.pathname, theme, skipAll, preview, originalReadEnabled],
  );
  // SAFETY: as above, useABTest returns these four fields on every path.
  const original = useABTest(
    CHALLENGE_CLARITY_HOME_EXPERIMENT.name,
    CHALLENGE_CLARITY_HOME_EXPERIMENT.featureFlag,
    UNASSIGNED_VARIANT,
    originalTracking,
  ) as ChallengeAssignment;
  // The held reading is unassigned, which is never a served arm. When the
  // fallback hook begins its live read, its prior state cannot manufacture a
  // control exposure while React commits the loading state.
  const decision = decideChallengeAssignment(abc, original);
  const flow = useRef<{
    design: ChallengeDesignArm;
    context?: ChallengeExperimentContext;
  } | null>(null);
  const flowPath = useRef(location.pathname);
  if (flowPath.current !== location.pathname || !isTarget || preview || existingRoutePreview) {
    flow.current = null;
    flowPath.current = location.pathname;
  }
  const [, setFlowRevision] = useState(0);

  // True when this page first rendered before PostHog answered. That visitor
  // has had a usable page from the start and keeps it (see below).
  const renderedWhileResolving = useRef(decision.status === 'resolving' && !preview).current;
  // Rob, 2026-09-23: "The register button should be ready from the moment the
  // page loads." While PostHog answers, which can take seconds on a slow phone
  // or behind an ad blocker, render the original page with a working form, not
  // a loading screen. It is the same tree every arm below renders, so when the
  // assignment lands on any arm the page component, and what the visitor
  // typed, stays. The old experiment is still never mounted before this one
  // resolves. If neither flag then serves an arm, the visitor keeps the
  // original page they already have rather than having it swapped for the
  // existing route (whose flag, 459166, is off, so it shows the same design).
  const resolving = decision.status === 'resolving' && !preview;
  const live = decision.status === 'assigned' ? decision : null;
  const assigned: ChallengeDesignArm | null = preview || (live ? live.arm : null);
  // A control preview on localhost or Dev still has to honor educationDesign.
  // Live control keeps the replacement page. Clarity Home stays the test arm.
  const educationDesignPreview =
    preview === 'control'
      ? normalizeEducationDesignVariant(searchParams.get('educationDesign'))
      : null;
  const design: ChallengeDesignArm = flow.current?.design || assigned || 'control';
  const active = Boolean(live && !preview && !live.isPreviewOverride);
  // Every Lead and PostHog event from the page spreads this, so each one says
  // which arm the visitor saw and which flag decided it.
  const decidingFlag = live && active ? live.experiment.featureFlag : null;
  let experimentContext: ChallengeExperimentContext | undefined;
  if (live && decidingFlag) {
    experimentContext = {
      experiment_name: live.experiment.name,
      experiment_variant: design,
      experiment_flag: decidingFlag,
      feature_flag: decidingFlag,
      is_experiment_active: true,
      experiment_version: getChallengeExperimentVersion(decidingFlag),
    };
    experimentContext[`$feature/${decidingFlag}`] = design;
  }
  if (flow.current) experimentContext = flow.current.context;
  useChallengeExperimentExposure(isTarget && !existingRoutePreview ? experimentContext : undefined);
  if (!isTarget || existingRoutePreview || educationDesignPreview) return <>{children}</>;
  if (!assigned && !resolving && !renderedWhileResolving && !flow.current) return <>{children}</>;
  const keepFormAssignment = (event: React.SyntheticEvent) => {
    if (
      preview ||
      flow.current ||
      !(event.target instanceof Element) ||
      !event.target.closest('form')
    )
      return;
    // An in-progress form keeps the design and attribution actually shown.
    // A person who starts before assignment stays outside the experiment for
    // this signup, including its later questions and confirmed conversion.
    flow.current = { design, context: experimentContext };
    setFlowRevision((revision) => revision + 1);
  };
  return (
    <div
      className={`education-design-experiment${design === 'test' ? ' challenge-home-review' : ''}`}
      data-testid="challenge-clarity-home-experiment"
      data-challenge-design={design}
      data-assignment={flow.current ? 'locked' : resolving ? 'resolving' : 'resolved'}
      data-experiment-active={String(Boolean(experimentContext))}
      data-experiment-flag={experimentContext?.experiment_flag}
      data-education-theme={theme}
      data-bs-theme={theme}
      onChangeCapture={keepFormAssignment}
      onSubmitCapture={keepFormAssignment}
    >
      <EducationDesignPreview variant={FORM_FIRST_ARMS.includes(design) ? 'form_first' : 'control'}>
        {/* C (form_first_ad_match) is B's page with the top Proven ad's heading
            and subheading (Rob, 2026-10-02). B and every other arm get null,
            which keeps their own copy. */}
        <ChallengeFormFirstCopyContext.Provider
          value={
            design === 'form_first_ad_match' ? resolveChallengeAdMatchCopy(location.search) : null
          }
        >
          {/* C (trimmed) and D (trimmed_headline) are A's page with the
              repeats removed (Rob, 2026-10-20). Every other arm gets null,
              which keeps its own page exactly as before. */}
          <ChallengeTrimmedCopyContext.Provider value={trimmedCopyFor(design)}>
            <NextChallengePage
              presentation={design === 'test' ? 'clarity-home' : undefined}
              experimentContext={experimentContext}
            />
          </ChallengeTrimmedCopyContext.Provider>
        </ChallengeFormFirstCopyContext.Provider>
      </EducationDesignPreview>
    </div>
  );
}
